<template>
    <div>
        <div class="row mb-2">
            <nav class="navbar navbar-expand primary-tab-bar w-100">
                <div class="container-fluid p-0">
                    <div class="collapse navbar-collapse">
                        <ul class="navbar-nav mb-lg-0 w-100 d-flex">
                            <li class="nav-item flex-fill">
                                <a 
                                    class="nav-link d-flex flex-nowrap primary-tab w-100 justify-content-center align-items-center" 
                                    :class="{ 'primary-tab-active': upload_option === 'upload' }"
                                    href="#" 
                                    role="button" 
                                    aria-expanded="false" 
                                    @click.prevent="upload_option = 'upload'"
                                >
                                    <span class="w-100 text-center d-flex justify-content-center align-items-center">Upload</span>
                                </a>
                            </li>
                            <li class="nav-item flex-fill">
                                <a 
                                    class="nav-link d-flex flex-nowrap primary-tab w-100 justify-content-center align-items-center" 
                                    :class="{ 'primary-tab-active': upload_option === 'public_link' }"
                                    href="#" 
                                    role="button" 
                                    aria-expanded="false" 
                                    @click.prevent="upload_option = 'public_link'"
                                >
                                    <span class="w-100 text-center d-flex justify-content-center align-items-center">Public Link</span>
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>
            </nav>
        </div>

        <div v-if="upload_option === 'upload'">
            <div class="row" :class="{ 'mb-2': !isInModal }">
                <p>Accepted File Types: .csv, .txt, and similar delimited text files.</p>
                <p >Once the file is dropped on the box area, the uploading process will start automatically.</p>
                <div class="my-2">
                    <FileDropzone 
                        :client-url="client.url"
                        :on-file-uploaded="handleFileUploaded"
                        @fileUploading="handleFileUploading"
                        @uploadProgress="handleUploadProgress"
                    />
                </div>
            </div>
            
            <!-- Recently Uploaded Files Table -->
            <div v-if="recent_files.length > 0" class="mt-4">
                <div class="card table-card">
                    <div class="card-header">
                        <div class="d-flex flex-row justify-content-between">
                            <div class="d-flex justify-content-start align-items-center flex-grow-1">
                                <h3 class="mb-0">Recently Uploaded Files ({{ visible_recent_files_length }})</h3>
                            </div>
                        </div>
                    </div>
                    <div class="card-body p-0">
                        <div class="table-width-responsive">  
                            <table class="table table-width-responsive table-without-pagination m-0">
                                <thead>
                                    <tr class="table-header-row">
                                        <th @click="sortByKey('name')" class="d-table-cell">
                                            <div class="d-flex flex-row">
                                                File Name
                                                <div class="d-flex flex-column">
                                                    <img 
                                                        class="sort-key" 
                                                        src="/img/icons/avid-icons/chevron-up.svg" 
                                                        :class="{ 'sort-key-active': sort_key === 'name' && sort_order === 'asc' }" alt=""
                                                    >
                                                    <img 
                                                        class="sort-key" 
                                                        src="/img/icons/avid-icons/chevron-down.svg" 
                                                        :class="{ 'sort-key-active': sort_key === 'name' && sort_order === 'des' }" alt=""
                                                    >                                                            
                                                </div>
                                            </div>
                                        </th>
                                        <th @click="sortByKey('status')" class="d-table-cell">
                                            <div class="d-flex flex-row">
                                                Status
                                                <div class="d-flex flex-column">
                                                    <img 
                                                        class="sort-key" 
                                                        src="/img/icons/avid-icons/chevron-up.svg" 
                                                        :class="{ 'sort-key-active': sort_key === 'status' && sort_order === 'asc' }" alt=""
                                                    >
                                                    <img 
                                                        class="sort-key" 
                                                        src="/img/icons/avid-icons/chevron-down.svg" 
                                                        :class="{ 'sort-key-active': sort_key === 'status' && sort_order === 'des' }" alt=""
                                                    >                                                        
                                                </div>
                                            </div>
                                        </th>
                                        <th class="d-none d-md-table-cell">Data Set</th>
                                        <th class="d-none d-md-table-cell">Audience</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(f, index) in filtered_sorted_recent_files" :key="f.id">
                                        <td class="ps-3 align-middle d-table-cell" style="max-width: 180px;">
                                            <div class="d-flex flex-column">
                                                <div style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap;" :title="f.name" data-bs-toggle="tooltip">
                                                    {{ f.name }}
                                                </div>
                                                <!-- Progress bar for uploading files -->
                                                <div v-if="f.status === 'processing' && upload_progress[f.name] !== undefined" class="mt-1">
                                                    <div class="progress" style="height: 10px;">
                                                        <div 
                                                            class="progress-bar" 
                                                            :style="{ width: upload_progress[f.name] + '%' }"
                                                            role="progressbar" 
                                                            :aria-valuenow="upload_progress[f.name]" 
                                                            aria-valuemin="0" 
                                                            aria-valuemax="100"
                                                        ></div>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td class="align-middle text-nowrap d-table-cell">
                                            <span v-if="f.id >= 0 && (f.status === 'ready' || f.status === 'invalid' || f.status === 'error')" class="badge" 
                                                :class=" getColorClass(f.status, f)">
                                                <span v-if="f.type === 'xlsx' && f.status === 'ready'">
                                                    <div class="d-inline-block spinner-border spinner-border-sm me-1" role="status"></div>
                                                    Extracting Worksheets...
                                                </span>
                                                <span v-else>
                                                    {{ propercase(f.status) }}
                                                    <span v-if="(f.status === 'invalid' || f.status === 'error') && f.status_msg" class="ms-1">
                                                        <i class="fa fa-question-circle" data-bs-toggle="tooltip"
                                                            :title="f.status_msg + (f.error_reference_id ? ' (Reference: ' + f.error_reference_id + ')' : '')"></i>
                                                    </span>
                                                </span>
                                            </span>
                                            <span v-else class="badge" 
                                            :class="getColorClass(f.status, f)">
                                                <div class="d-inline-block spinner-border spinner-border-sm ms-2" role="status"></div>
                                                <span class="ms-1">
                                                    {{ getStatusText(f.status, f) }}...    
                                                </span>
                                            </span>
                                        </td>
                                        <td class="align-middle d-none d-md-table-cell">
                                            <span v-if="f.dataset">
                                                {{ propercase(f.dataset.name) }}
                                            </span>
                                            <a 
                                                v-else-if="f.id >= 0 && f.status === 'ready' && isDataFile(f)" 
                                                class="btn btn-sm btn-primary text-nowrap" 
                                                :href="`/integrations/${client.url}/mapping/file/${f.id}`"
                                            >
                                                <i class="fa fa-plus"></i> Create a Data Set
                                            </a>
                                            <span v-else><em>N/A</em></span>
                                        </td>
                                        <td class="align-middle d-none d-md-table-cell">
                                            <button v-if="f.id >= 0 && f.status === 'ready' && isDataFile(f)"
                                                :disabled="form.saving"
                                                class="btn btn-sm text-nowrap btn-secondary px-2 py-1" @click="addAudience(f)"
                                            >
                                                <span v-if="form.saving">
                                                    <div class="d-inline-block spinner-border spinner-border-sm me-1" role="status">
                                                        <span class="visually-hidden">Loading...</span>
                                                    </div>
                                                    Creating...
                                                </span>
                                                <span v-else>
                                                    <i class="fa fa-plus"></i> Add an Audience
                                                </span>
                                            </button>
                                            <span v-else><em>N/A</em></span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>                            
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="isInModal" class="w-100 d-flex flex-row justify-content-center gap-3 mt-3">        
                <button class="btn btn-secondary w-100" 
                    @click.prevent="closeModal"
                >
                    <span><img class="icon" src="/img/icons/avid-icons/close.svg" alt=""> Cancel</span>
                </button>
            </div>                        
        </div>

        <div v-if="upload_option === 'public_link'">
            <div class="row">
                <h4>Public Upload Link</h4>
                <p>Enabling a public link will allow you, or other users, to upload data files without having to login. You'll be given a secure link and users will only be able to add new files; they cannot view or edit existing ones. </p>
                <div class="col-12 my-2" id="public-links">
                    <div class="input-group">
                        <button 
                            class="btn btn-secondary" 
                            @click="toggleLink" 
                            v-if="local_conn?.config_data.public_link"
                        >
                            <span>Disable Public Upload Link</span>
                        </button>
                        <button 
                            class="btn btn-primary" 
                            @click="toggleLink" 
                            v-else
                        >
                            <span>Enable Public Upload Link</span>
                        </button>
                        <transition name="slide-slow">
                            <input 
                                v-if="local_conn?.config_data.public_link"
                                type="text" 
                                class="form-control ps-2 text-muted" 
                                :value="fullFileUrl" 
                                @click="copyToClipboard"
                                ref="clone" 
                                readonly 
                                @focus="$event.target.select()"
                             aria-label="Text input">
                        </transition>
                    </div>
                </div>

                <div v-if="isInModal" class="w-100 d-flex flex-row justify-content-center gap-3 mt-3">        
                    <button class="btn btn-secondary w-100" @click.prevent="closeModal">
                        <img class="icon" src="/img/icons/avid-icons/close.svg" alt=""> Cancel
                    </button>
                </div>  
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import FileDropzone from '../components/FileDropzone.vue';
import Swal from 'sweetalert2';
import { useNotifications } from '../../composables/useNotifications';

export default defineComponent({
    name: 'FileModal',
    components: {
        FileDropzone
    },
    setup() {
        const { showNotification } = useNotifications();
        return {
            showNotification
        };
    },
    props: {
        client: {
            type: Object,
            required: true
        },
        connection: {
            type: Object,
            required: true
        },
        isInModal: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            upload_option: 'upload',
            if_uploaded: false, // for file upload only
            local_conn: null,
            selected_integration_info: {},
            form: {
                saving: false,
                error: false,
                msg: "",
                loading: false
            },
            recent_files: [],
            sort_key: 'name',
            sort_order: 'asc',
            upload_progress: {} // Track upload progress by filename
        };
    },
    beforeMount() {
        this.listenForFileUpdates();
        this.local_conn = this.connection;
        //console.log(this.local_conn);
        if(!this.local_conn.config_data || this.local_conn.config_data.public_link === undefined) {
            this.local_conn.config_data = {
                'public_link': false
            };
        }
    },
    mounted() {
        // Initialize tooltips
        this.initTooltips();
        
        // Add navigation warning for processing files
        this.handleBeforeUnload = this.handleBeforeUnload.bind(this);
        window.addEventListener('beforeunload', this.handleBeforeUnload);
    },
    updated() {
        // Re-initialize tooltips after component updates
        this.$nextTick(() => {
            this.initTooltips();
        });
    },
    computed: {
        file_url() {
            return `${window.location.protocol}//${window.location.host}/f/`;
        },
        fullFileUrl() {
            return `${this.file_url}${this.client.url}`;
        },
        sorted_recent_files() {
            return [...this.recent_files].sort((a, b) => {
                if (this.sort_key === 'dataset') {
                    const aName = a.dataset ? a.dataset.name.toLowerCase() : '';
                    const bName = b.dataset ? b.dataset.name.toLowerCase() : '';
                    return this.sort_order === 'asc' 
                        ? aName.localeCompare(bName)
                        : bName.localeCompare(aName);
                }
                
                let aVal = a[this.sort_key];
                let bVal = b[this.sort_key];
                
                if (this.sort_key === 'created_at') {
                    aVal = new Date(aVal);
                    bVal = new Date(bVal);
                }

                return this.sort_order === 'asc'
                    ? aVal < bVal ? -1 : aVal > bVal ? 1 : 0
                    : aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
            });
        },
        filtered_sorted_recent_files() {
            return this.sorted_recent_files.filter(f => !this.shouldHideFile(f));
        },
        visible_recent_files_length() {
            return this.recent_files.filter(f => !this.shouldHideFile(f)).length;
        }
    },
    beforeUnmount() {
        // Clean up Pusher subscription
        window.Echo.leaveChannel(`file.${this.client.url}`);
        
        // Remove navigation warning for processing files
        window.removeEventListener('beforeunload', this.handleBeforeUnload);
    },
    methods: {
        initTooltips() {
            // Destroy existing tooltips first to prevent duplicates and ensure clean state
            const existingTooltips = document.querySelectorAll('[data-bs-toggle="tooltip"]');
            existingTooltips.forEach(el => {
                const tooltip = window.bootstrap.Tooltip.getInstance(el);
                if (tooltip) {
                    tooltip.dispose();
                }
            });

            // Initialize new tooltips
            const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
            const tooltipList = [...tooltipTriggerList].map(tooltipTriggerEl => new window.bootstrap.Tooltip(tooltipTriggerEl));

            // Add event listeners for hiding tooltips
            for (var i = 0; i < tooltipTriggerList.length; i++) {
                (function(index) {
                    tooltipTriggerList[index].addEventListener('click', function() {
                        // Hide the tooltip using the index passed to the closure
                        tooltipList[index].hide();
                    });
                    tooltipTriggerList[index].addEventListener('mouseleave', function() {
                        // Hide the tooltip using the index passed to the closure
                        tooltipList[index].hide();
                    });
                })(i);
            }
        },
        listenForFileUpdates() {
            // Subscribe to file status updates
            window.Echo.channel(`file.${this.client.url}`)
                .listen('FileStatusUpdated', (e) => {
                    // Update file in recent_files if it exists
                    const index = this.recent_files.findIndex(f => f.id === e.id);
                    if (index !== -1) {
                        // Use Vue.set or array splice to ensure reactivity
                        this.recent_files.splice(index, 1, {
                            ...this.recent_files[index],
                            ...e
                        });
                        
                        // If this is an xlsx file that just became ready, schedule it for delayed hiding
                        if (e.type === 'xlsx' && e.status === 'ready') {
                            this.scheduleXlsxFileHiding(e.id);
                        }
                    } else {
                        // If not found by ID, check for files with same name and 'processing' status
                        const processingFiles = this.recent_files.filter(f => 
                            f.name === e.name 
                        );
                        
                        if (processingFiles.length > 0) {
                            // Sort by created_at to get the oldest one
                            processingFiles.sort((a, b) => new Date(a.lastModified) - new Date(b.lastModified));
                            const oldestProcessingFile = processingFiles[0];
                            
                            // Find the index of the oldest processing file
                            const oldestIndex = this.recent_files.findIndex(f => f.id === oldestProcessingFile.id);
                            
                            if (oldestIndex !== -1) {
                                // Update the oldest processing file with the new data
                                this.recent_files.splice(oldestIndex, 1, {
                                    ...oldestProcessingFile,
                                    ...e
                                });
                            } else {
                                this.recent_files.unshift(e);
                            }
                        } else {
                            this.recent_files.unshift(e);
                        }
                    }
                    
                    // Re-initialize tooltips after file updates
                    this.$nextTick(() => {
                        this.initTooltips();
                    });
                });
        },
        toggleLink() {
            this.local_conn.config_data.public_link = !this.local_conn.config_data.public_link;
            //Save it to the DB
            window.axios.post(`/api/connection/${this.local_conn.id}`, this.local_conn);
        },
        copyToClipboard() {
            this.$refs.clone.focus();
            document.execCommand('copy');
            this.showNotification({
                text: 'Link copied to clipboard!'
            });
        },
        handleFileUploaded(file) {
            this.if_uploaded = true;
            // Clean up progress tracking for completed upload
            if (this.upload_progress[file.name]) {
                delete this.upload_progress[file.name];
            }
            
            // Emit the uploaded file to parent component so it can be added to the main file list
            this.$emit('fileUploaded', file);
            
            // Re-initialize tooltips after file upload completion
            this.$nextTick(() => {
                this.initTooltips();
            });
        },
        handleFileUploading(file){
            file.status = 'processing';
            file.progress = 0; // Initialize progress
            this.recent_files.unshift(file);
            // Initialize progress tracking
            this.upload_progress[file.name] = 0;
            
            // Emit the uploading file to parent component so it can be added to the main file list
            this.$emit('fileUploading', file);
            
            // Re-initialize tooltips after adding new file
            this.$nextTick(() => {
                this.initTooltips();
            });
        },
        handleUploadProgress(progressData) {
            // Update progress for the file being uploaded
            this.upload_progress[progressData.fileName] = progressData.progress;
            
            // Also update the file in recent_files if it exists
            const index = this.recent_files.findIndex(f => f.name === progressData.fileName);
            if (index !== -1) {
                this.recent_files[index].progress = progressData.progress;
            }
            
            // Emit progress update to parent component
            this.$emit('uploadProgress', progressData);
            
            // Re-initialize tooltips after progress updates
            this.$nextTick(() => {
                this.initTooltips();
            });
        },
        closeModal() {
            this.$emit('closeModal');
        },
        getColorClass(word, file) {
            if(word === 'received' || word === 'processing' || word === 'analyzing') {
                return 'badge-warning';
            }
            if(word === 'ready') {
                // If it's an xlsx file that's ready, it's actually extracting worksheets, so show warning
                if (file && file.type === 'xlsx') {
                    return 'badge-warning';
                }
                return 'bg-green bg-icon-success';
            }
            return 'bg-red bg-icon-danger';
        },
        isDataFile(file) {
            return ['csv', 'tsv', 'tab', 'txt'].includes(file.type);
        },
        sortByKey(key) {
            if (this.sort_key === key) {
                this.sort_order = this.sort_order === 'asc' ? 'des' : 'asc';
            } else {
                this.sort_key = key;
                this.sort_order = 'asc';
            }
            
            // Re-initialize tooltips after sorting
            this.$nextTick(() => {
                this.initTooltips();
            });
        },
        handleBeforeUnload(event) {
            // Check if there are any files still processing
            const hasProcessingFiles = this.recent_files.some(file => !['ready', 'invalid', 'error'].includes(file.status));
            
            if (hasProcessingFiles) {
                // Show SweetAlert confirmation
                Swal.fire({
                    title: 'Files Still Processing',
                    text: 'Some files are still being processed. Navigating away will cancel the upload. Are you sure you want to continue?',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Leave Anyway',
                    cancelButtonText: 'Stay on Page',
                    customClass: {
                        popup: 'swal-warning'
                    },
                    allowOutsideClick: false,
                    allowEscapeKey: false
                }).then((result) => {
                    if (result.isConfirmed) {
                        // Allow navigation
                        window.removeEventListener('beforeunload', this.handleBeforeUnload);
                        window.location.href = window.location.href;
                    }
                });
                
                // Prevent default navigation
                event.preventDefault();
                event.returnValue = '';
                return false;
            }
        },
        addAudience(file) {
            this.form.saving = true;
            //Call the API to create a segment from the file
            window.axios.post(`/api/segment/${this.client.url}/file/${file.id}`)
                .then(response => {
                    this.form.saving = false;
                    //Redirect to the segment page
                    window.location.href = `/audience/${this.client.url}/${response.data.segment.id}?file_segment=true`;
                })
                .catch(error => {
                    this.form.saving = false;
                    if(error.response.status === 400) {
                        //Show a swal error message
                        Swal.fire({
                            title: 'Error Creating Audience',
                            text: "We could not find a column in your data that matches the ID column in your CRM. You'll be redirected to the Audience Creation page where you can create the mapping manually.",
                            icon: 'error',
                            customClass: {
                                popup: 'swal-error'
                            }
                        }).then(() => {
                            window.location.href = `/audience/${this.client.url}/new`;
                        });
                    } else if(error.response.status === 404) {
                        //Show a swal error message
                        Swal.fire({
                            title: 'Finish Mapping Your Data',
                            text: "Before we can build audiences from your uploaded files, we need to finalize your CRM integration. Please contact Avid support for additional help.",
                            icon: 'error',
                            customClass: {
                                popup: 'swal-error'
                            }
                        });
                    } else {
                        //Show a swal error message
                        Swal.fire({
                            title: 'Error Creating Audience',
                            text: "There was an error creating your audience. Please try again shortly.",
                            icon: 'error',
                            customClass: {
                                popup: 'swal-error'
                            }
                        });
                    }
                });
        },
        propercase(str) {
            if (!str) return '';
            return str.replace(/\w\S*/g, function(txt) {
                return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
            });
        },
        getStatusText(status, file) {
            if (status === 'processing') {
                return 'Uploading';
            }
            if (status === 'received' && this.isDataFile(file)) {
                return 'Loading Data';
            }
            return this.propercase(status);
        },
        shouldHideFile(file) {
            // Hide zip files when they are ready (after extraction/processing)
            // For xlsx files, we don't hide them immediately since we need to wait for CSV files to be visible
            return file.type === 'zip' && file.status === 'ready';
        },
        scheduleXlsxFileHiding(fileId) {
            // Wait a bit for CSV files to be uploaded, then check if we should hide the xlsx
            setTimeout(() => {
                const index = this.recent_files.findIndex(f => f.id === fileId);
                if (index !== -1) {
                    const xlsxFile = this.recent_files[index];
                    
                    // Check if there are CSV files that might have been extracted from this xlsx
                    // Look for CSV files with similar names (without extension) that were uploaded recently
                    const xlsxNameWithoutExt = xlsxFile.name.replace(/\.xlsx?$/i, '');
                    const recentCsvFiles = this.recent_files.filter(f => 
                        f.type === 'csv' && 
                        f.name.includes(xlsxNameWithoutExt) &&
                        f.source === 'Upload' &&
                        Math.abs(new Date(f.created_at) - new Date(xlsxFile.created_at)) < 60000 // Within 1 minute
                    );
                    
                    // If we found CSV files that were likely extracted from this xlsx, hide the xlsx file
                    if (recentCsvFiles.length > 0) {
                        this.recent_files.splice(index, 1);
                        // Re-initialize tooltips after hiding
                        this.$nextTick(() => {
                            this.initTooltips();
                        });
                    }
                }
            }, 2000); // Wait 2 seconds for CSV files to be uploaded
            
            // Fallback: hide the xlsx file after 10 seconds regardless, to prevent it from staying visible indefinitely
            setTimeout(() => {
                const index = this.recent_files.findIndex(f => f.id === fileId);
                if (index !== -1) {
                    this.recent_files.splice(index, 1);
                    // Re-initialize tooltips after hiding
                    this.$nextTick(() => {
                        this.initTooltips();
                    });
                }
            }, 10000); // Fallback after 10 seconds
        }
    }
});
</script>
